import { AppPushConfig } from './AppPushConfig';
import { InstantPushConfig } from './InstantPushConfig';
import { RecurringTrigger } from './trigger/RecurringTrigger';
import { Constants } from '../../common/Constants';
import { PushType } from '../../common/EnumTypes';
import * as InternalTypes from '../../common/InternalTypes';
import { Logger } from '../../common/Logger';
import { LoggerTexts } from '../../common/LoggerTexts';
import { ScopeCheck } from '../../common/ScopeCheck';
import { Util } from '../../common/Util';

export class PushConfig {
	private readonly _startupConfig: InternalTypes.StartupConfig;
	private _timedPush?: InternalTypes.TimedPush;
	private _appPush?: InternalTypes.AppPush;
	private _instantPush: InternalTypes.InstantPush;
	private readonly _generalPushInfo?: InternalTypes.PushConfigGeneralInfo;

	private _quietPeriodInHrs: number;
	private _snoozePeriodInHrs: number;

	private _intervalInitialTimedPushInDays: number;
	private _deviationInitialTimedPushInDays: number;
	private _intervalInitialAppPushInDays: number;
	private _deviationInitialAppPushInDays: number;
	private _intervalNextTimedPushInDays: number;
	private _deviationNextTimedPushInDays: number;
	private _intervalNextAppPushInDays: number;
	private _deviationNextAppPushInDays: number;

	private _instantPushDailyLimit: number;

	private _intervalTimerInMinutes: number;
	private _minimumTimeToStartTimerInHrs: number;
	private readonly _immediateTestValue: number = 0;
	private readonly _immediateTestTimedPushInterval: number = 2;
	private readonly _immediateTestInstantPushDailyLimit: number = 99;
	private readonly _immediateTestInstantPushQuietPeriodInHrs: number = 0;

	private _appPushConfigs: Map<string, AppPushConfig>;
	private _instantPushConfigs: Map<string, InstantPushConfig>;
	private readonly _logger: Logger;

	constructor(startupConfig: InternalTypes.StartupConfig, pushConfig?: InternalTypes.PxPushConfig) {
		this._logger = new Logger(Constants.FILE_PUSH_CFG);

		if (!startupConfig) {
			throw new Error(LoggerTexts.PUSH_CFG.CTOR_STARTUPCFG_CANNOT_BE_NULL);
		}
		this._startupConfig = startupConfig;

		if (pushConfig?.general) {
			this._generalPushInfo = pushConfig.general;
		}
		if (pushConfig?.timedPush) {
			this._timedPush = pushConfig.timedPush;
		}
		if (pushConfig?.appPush) {
			this._appPush = pushConfig.appPush;
		}
		if (pushConfig?.instantPush) {
			this._instantPush = pushConfig.instantPush;
		}
		if (!ScopeCheck.isTestModeActive()) {
			this._applyLoadedConfig();
		} else {
			this._activateTestMode();
		}

		if (pushConfig?.appPush?.configs) {
			this._parseAppPushConfigs(pushConfig.appPush.configs);
		}

		if (pushConfig?.instantPush?.configs) {
			this._parseInstantPushConfigs(pushConfig.instantPush.configs);
		}
	}

	_applyLoadedConfig() {
		this._preparePushConfigGeneralInfo();
		this._prepareTimedPushInfo();
		this._prepareAppPushInfo();
		this._prepareInstantPushInfo();
	}

	private _preparePushConfigGeneralInfo() {
		this._quietPeriodInHrs = this._generalPushInfo?.quietPeriodInHrs ?? Constants.DEFAULTS.PUSH_GENERAL_QUIET_PERIOD_IN_HRS;
		this._snoozePeriodInHrs = this._generalPushInfo?.snoozePeriodInHrs ?? Constants.DEFAULTS.PUSH_GENERAL_SNOOZE_PERIOD_IN_HRS;
	}

	private _prepareTimedPushInfo() {
		if (ScopeCheck.isTimedPushAvailable(this._startupConfig)) {
			if (!this._timedPush) {
				this._timedPush = {} as InternalTypes.TimedPush;
			}
			this._intervalTimerInMinutes = this._timedPush.intervalTimerInMinutes ?? Constants.DEFAULTS.TIMED_PUSH_INTERVAL_TIMER_IN_MINS;
			this._minimumTimeToStartTimerInHrs =
				this._timedPush.minimumTimeToStartTimerInHrs ?? Constants.DEFAULTS.TIMED_PUSH_MIN_TIME_TO_START_TIMER_IN_HRS;

			this._intervalInitialTimedPushInDays =
				this._timedPush.intervalInitialPushInDays ?? Constants.DEFAULTS.TIMED_PUSH_INTERVAL_INIT_PUSH_IN_DAYS;
			this._deviationInitialTimedPushInDays =
				this._timedPush.deviationInitialPushInDays ?? Constants.DEFAULTS.TIMED_PUSH_DEVIATION_INIT_PUSH_IN_DAYS;

			this._intervalNextTimedPushInDays = this._timedPush.intervalNextPushInDays ?? Constants.DEFAULTS.TIMED_PUSH_INTERVAL_NEXT_PUSH_IN_DAYS;
			this._deviationNextTimedPushInDays = this._timedPush.deviationNextPushInDays ?? Constants.DEFAULTS.TIMED_PUSH_DEVIATION_NEXT_PUSH_IN_DAYS;
		}
	}

	private _prepareAppPushInfo() {
		if (ScopeCheck.isAppPushAvailable(this._startupConfig)) {
			if (!this._appPush) {
				this._appPush = {} as InternalTypes.AppPush;
			}
			this._intervalInitialAppPushInDays = this._appPush.intervalInitialPushInDays ?? Constants.DEFAULTS.APP_PUSH_INTERVAL_INIT_PUSH_IN_DAYS;
			this._deviationInitialAppPushInDays = this._appPush.deviationInitialPushInDays ?? Constants.DEFAULTS.APP_PUSH_DEVIATION_INIT_PUSH_IN_DAYS;
			this._intervalNextAppPushInDays = this._appPush.intervalNextPushInDays ?? Constants.DEFAULTS.APP_PUSH_INTERVAL_NEXT_PUSH_IN_DAYS;
			this._deviationNextAppPushInDays = this._appPush.deviationNextPushInDays ?? Constants.DEFAULTS.APP_PUSH_DEVIATION_NEXT_PUSH_IN_DAYS;
		}
	}

	private _prepareInstantPushInfo() {
		if (ScopeCheck.isInstantPushAvailable(this._startupConfig)) {
			if (!this._instantPush) {
				this._instantPush = {} as InternalTypes.InstantPush;
			}
			this._instantPushDailyLimit = this._instantPush?.dailyLimit;
		}
	}

	_activateTestMode() {
		this._quietPeriodInHrs = this._immediateTestValue;
		this._snoozePeriodInHrs = this._immediateTestValue;

		this._intervalInitialTimedPushInDays = this._immediateTestValue;
		this._deviationInitialTimedPushInDays = this._immediateTestValue;

		this._intervalInitialAppPushInDays = this._immediateTestValue;
		this._deviationInitialAppPushInDays = this._immediateTestValue;

		this._intervalNextTimedPushInDays = this._immediateTestValue;
		this._deviationNextTimedPushInDays = this._immediateTestValue;

		this._intervalNextAppPushInDays = this._immediateTestValue;
		this._deviationNextAppPushInDays = this._immediateTestValue;

		this._intervalTimerInMinutes = this._immediateTestTimedPushInterval;
		this._minimumTimeToStartTimerInHrs = this._timedPush?.minimumTimeToStartTimerInHrs || 0;

		this._instantPushDailyLimit = this._immediateTestInstantPushDailyLimit;
	}

	get quietPeriodInHrs(): number {
		return this._quietPeriodInHrs;
	}
	get snoozePeriodInHrs(): number {
		return this._snoozePeriodInHrs;
	}
	get intervalInitialTimedPushInDays(): number {
		return this._intervalInitialTimedPushInDays;
	}
	get deviationInitialTimedPushInDays(): number {
		return this._deviationInitialTimedPushInDays;
	}
	get intervalInitialAppPushInDays(): number {
		return this._intervalInitialAppPushInDays;
	}
	get deviationInitialAppPushInDays(): number {
		return this._deviationInitialAppPushInDays;
	}
	get intervalNextTimedPushInDays(): number {
		return this._intervalNextTimedPushInDays;
	}
	get deviationNextTimedPushInDays(): number {
		return this._deviationNextTimedPushInDays;
	}
	get intervalNextAppPushInDays(): number {
		return this._intervalNextAppPushInDays;
	}
	get deviationNextAppPushInDays(): number {
		return this._deviationNextAppPushInDays;
	}
	get intervalTimerInMinutes(): number {
		return this._intervalTimerInMinutes;
	}
	get minimumTimeToStartTimerInHrs(): number {
		return this._minimumTimeToStartTimerInHrs;
	}
	get appPushConfigs() {
		return this._appPushConfigs;
	}
	get instantPush(): InternalTypes.InstantPush {
		return this._instantPush;
	}
	get instantPushDailyLimit(): number {
		return this._instantPushDailyLimit;
	}
	get instantPushConfigs() {
		return this._instantPushConfigs;
	}

	_parseAppPushConfigs(appPushConfigs: InternalTypes.AppPushConfig[]) {
		this._appPushConfigs = new Map();
		appPushConfigs.forEach((appPushConfig: InternalTypes.AppPushConfig) => {
			const appPushTrigger = appPushConfig.trigger;
			const triggerType = appPushTrigger.type;
			const trigger = new RecurringTrigger(appPushTrigger);
			const newAppPushConfig = new AppPushConfig();
			newAppPushConfig.initFromJsonObject(appPushConfig);
			newAppPushConfig.triggerType = triggerType;
			newAppPushConfig.triggerConfig = trigger;
			this._addAppPushConfig(newAppPushConfig);
		});
	}

	private _addAppPushConfig(appPushConfig: AppPushConfig) {
		this._appPushConfigs.set(appPushConfig.combinedKey, appPushConfig);
	}

	findAppPushConfigById(areaId: string, triggerName: string): AppPushConfig | undefined {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		return this._getPushConfigById(combinedKey, PushType.appPush) as AppPushConfig;
	}

	findExpiredAppPushConfigs(): AppPushConfig[] {
		const expiredAppPushConfigs: AppPushConfig[] = [];
		this._appPushConfigs?.forEach((item) => {
			if (item.isExpired()) {
				expiredAppPushConfigs.push(item);
			}
		});
		return expiredAppPushConfigs;
	}

	private _parseInstantPushConfigs(instantPushConfigs: InternalTypes.InstantPushConfig[]) {
		this._instantPushConfigs = new Map();
		instantPushConfigs.forEach((instantPushConfig: InternalTypes.InstantPushConfig) => {
			const newInstantPushConfig = new InstantPushConfig();
			if (ScopeCheck.isTestModeActive()) {
				instantPushConfig.quietPeriodInHrs = this._immediateTestInstantPushQuietPeriodInHrs;
			}
			newInstantPushConfig.initFromJsonObject(instantPushConfig);
			this._instantPushConfigs.set(newInstantPushConfig.combinedKey, newInstantPushConfig);
		});
	}

	findInstantPushConfigById(areaId: string, triggerName: string): InstantPushConfig | undefined {
		const combinedKey = Util.getCombinedKey(areaId, triggerName);
		return this._getPushConfigById(combinedKey, PushType.instantPush) as InstantPushConfig;
	}

	private _getPushConfigById(combinedKey: string, pushType: PushType): AppPushConfig | InstantPushConfig | undefined {
		let pushConfig: AppPushConfig | InstantPushConfig | undefined;
		let inactivePushConfigMessage: string = '';
		let pushConfigNotFoundMessage: string = '';
		if (pushType === PushType.appPush) {
			pushConfig = this._appPushConfigs?.get(combinedKey);
			inactivePushConfigMessage = LoggerTexts.PUSH_CFG.INACTIVE_APP_PUSH_CFG;
			pushConfigNotFoundMessage = LoggerTexts.PUSH_CFG.APP_PUSH_CFG_NOT_FOUND;
		}
		if (pushType === PushType.instantPush) {
			pushConfig = this._instantPushConfigs?.get(combinedKey);
			inactivePushConfigMessage = LoggerTexts.PUSH_CFG.INACTIVE_INSTANT_PUSH_CFG;
			pushConfigNotFoundMessage = LoggerTexts.PUSH_CFG.INSTANT_PUSH_CFG_NOT_FOUND;
		}
		if (pushConfig) {
			if (pushConfig?.isActive()) {
				return pushConfig;
			} else {
				this._logger.info(inactivePushConfigMessage);
			}
		} else {
			this._logger.info(pushConfigNotFoundMessage);
		}
		return undefined;
	}
}
